import { useState, useRef } from 'react'
import { Formik, ErrorMessage, Field } from 'formik';
import * as Yup from "yup";
import { useNavigate } from 'react-router-dom';
import GenreChooser from "./Components/GenreChooser";
import TagChooser from "./Components/TagChooser";
import DirectorAutocomplete from './Components/DirectorAutocomplete';
import ModalLoading from './Components/ModalLoading';
import { ErrorBanner } from './Components/Banners';
import { useFirestore, useAuth } from 'reactfire';
import { getApp } from "firebase/app";
import { addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from "firebase/storage";
import Resizer from "react-image-file-resizer";
import cloneDeep from 'lodash/cloneDeep';
import { PlusIcon } from '@heroicons/react/20/solid'
import AddProducerModal from './Components/AddProducerModal';
import AddActorModal from './Components/AddActorModal';
import AddWatchModal from './Components/AddWatchModal';
import CountryChooser from './Components/CountryChooser';
import LanguageChooser from './Components/LanguageChooser';
import { extractYoutubeId, extractTmdbPath, extractImdbId, extractTmdbTvPath, extractTmdbSeasonNumber } from './Functions/urlParsing.js';
import { findSimilarTitles } from './Functions/duplicateTitleCheck.js';
import { useAllTitlesLite } from './Hooks/useAllTitlesLite';
import { fetchTmdbMovieDetails, fetchTmdbTvSeasonDetails } from './Functions/tmdbService.js';
import { findOrCreatePeople } from './Functions/personLookup.js';

// https://firebase.google.com/docs/storage/web/start re multiple buckets
// https://stackoverflow.com/questions/56149756/reactjs-how-to-handle-image-file-upload-with-formik

// 2026-08-19: human-readable labels for the "Needs: ..." diagnostic next to Create title,
// so an invalid field shows as e.g. "Poster" rather than the raw Formik key "posterShadow".
const FIELD_LABELS = {
    titleDisplay: 'Display title',
    trailer: 'Youtube trailer ID',
    idTMDB: 'Movie Database ID',
    rating: 'Rating',
    posterShadow: 'Poster',
    kind: 'Kind',
    directorsGuids: 'Director(s)',
    writersGuids: 'Writer(s)',
    genres: 'Genre (at least one)',
    tags: 'Tag (at least one)',
    idIMDB: 'IMDB ID',
    season: 'Season',
};

export default function TitleAddPage({ authUser }) {
    // STATE

    // eslint-disable-next-line
    const [debugShowFormErrors, setDebugShowFormErrors] = useState(false);
    // eslint-disable-next-line
    const [artworkImgEncoded, setArtworkImgEncoded] = useState(undefined);
    const [selectedProducers, setSelectedProducers] = useState([]);
    const [selectedDirectors, setSelectedDirectors] = useState([]);
    const [selectedWriters, setSelectedWriters] = useState([]);
    const [selectedCinematographers, setSelectedCinematographers] = useState([]);
    const [selectedEditors, setSelectedEditors] = useState([]);
    const [selectedComposers, setSelectedComposers] = useState([]);
    // 2026-08-20: Showrunner(s) and Creator(s) merged into this one list -- see the
    // comment in handleSubmit for why (only `creators` is ever rendered by either
    // shipped app).
    const [selectedCreators, setSelectedCreators] = useState([]);
    const [selectedCast, setSelectedCast] = useState([]);
    const [selectedWatch, setSelectedWatch] = useState([]);
    const [artworkImgFile, setArtworkImgFile] = useState(undefined);
    const [modalSpinnerOpen, setModalSpinnerOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [modalMessage, setModalMessage] = useState(undefined);
    const [modalProducerOpen, setModalProducerOpen] = useState(false);
    const [modalActorOpen, setModalActorOpen] = useState(false);
    const [modalWatchOpen, setModalWatchOpen] = useState(false);
    const [useSeriesMetadata, setUseSeriesMetadata] = useState(false);
    // 2026-08-19: duplicate-title warning (see Functions/duplicateTitleCheck.js) -- a
    // WARNING, not a hard block. selectedProducers etc. above are unrelated.
    const [duplicateWarnings, setDuplicateWarnings] = useState([]);
    // 2026-08-19: live TMDB auto-fill for the bulk title-entry push (see
    // Functions/tmdbService.js). undefined | 'loading' | 'success' | 'error'.
    const [tmdbFetchStatus, setTmdbFetchStatus] = useState(undefined);
    const [tmdbFetchNote, setTmdbFetchNote] = useState(undefined);

    // HOOKS
    const firebaseApp = getApp();
    const storage = getStorage(firebaseApp, "gs://findflix-cdn");
    const auth = useAuth();
    const firestore = useFirestore();
    const navigate = useNavigate();
    const artworkImgRef = useRef()
    const ratingFieldRef = useRef();
    const titleDisplayRef = useRef();
    const synopsisRef = useRef();
    const runtimeHHRef = useRef();
    const runtimeMMRef = useRef();
    const releasedRef = useRef();
    const idIMDBRef = useRef();
    // 2026-08-20: lets handleSeasonFieldChange visually update the TMDB field's
    // displayed path (to include /season/N) even though it's an uncontrolled input --
    // same pattern as the other refs here.
    const idTMDBRef = useRef();
    // 2026-08-20: numEpisodes was another uncontrolled input with no ref -- same bug
    // class as idTMDB above. setFieldValue alone updated Formik's state (so it would
    // have actually saved correctly) but never repainted the visible field, which is
    // what Chris was seeing as "not pulling through" in testing.
    const numEpisodesRef = useRef();
    const allTitlesLite = useAllTitlesLite();

    // FORM
    const initialValues = {
        titleDisplay: undefined,
        genres: [],
        tags: [],
        synopsis: undefined,
        kind: '1',
        // 2026-08-20: TV series support -- required only when kind is Series, see
        // validationSchema's .when() below. Plain string (Formik-controlled select,
        // not an uncontrolled ref like most fields here) since there's no separate
        // "shadow" concern -- it's a real, directly-submitted field. Always a plain
        // numeric string ("0" for Specials, otherwise "1"-"99") -- the select's OWN
        // displayed selection (which also needs to distinguish "Limited Series" from
        // a plain Season 1, even though both store season=1) lives separately in
        // seasonSelection below.
        season: undefined,
        // 2026-08-29 (Chris, urgent): drives the Season <select>'s displayed value --
        // '', 'limited', 'specials', or a plain '1'-'99' string. Kept separate from
        // `season` because Limited Series and a plain Season 1 both store season=1,
        // so the select needs its own state to show the right option as selected.
        seasonSelection: undefined,
        // 2026-08-29 (Chris, urgent): true only when "Limited Series" is chosen --
        // TMDB has no season-level concept of this (a limited series is just Season 1
        // under the hood), so this is a pure label override. Only ever written to the
        // Firestore doc when true (see handleSubmit) -- absent otherwise, same sparse-
        // field convention as the rest of this schema.
        isLimitedSeries: false,
        released: undefined,
        runtime: undefined,
        rating: undefined,
        idJW: undefined,
        idRT: undefined,
        idTMDB: undefined,
        idWiki: undefined,
        trailer: undefined,
        country: undefined,
        language: undefined,
        // 2026-08-18: defaults to FindFlix-only per Pascal (2026-08-18, after reviewing
        // the preview deploy) -- FindFlix is the active brand being built out day to day.
        // Check "BlackPhlix" too for a dual-brand title.
        channelBlackphlix: false,
        channelFindflix: true,
        // 2026-08-19: shadow fields synced from selectedDirectors/selectedWriters (see
        // handleDirectorChange/handleWriterChange/clear*Pressed below) so Yup can actually
        // enforce "at least 1" -- neither was previously wired into Formik validation at
        // all, so Director was silently unenforced despite looking required in the UI.
        directorsGuids: [],
        writersGuids: [],
        // 2026-08-19: TEMPORARY BULK-ENTRY MODE (Chris x Pascal, 2026-08-19) -- tracks
        // whether a poster is queued for upload (manual selection or TMDB auto-fill), so
        // Yup can enforce it below. This whole block of requiredness changes is meant to
        // be reverted once the 3000-title push is done -- see the matching comment on
        // validationSchema.
        posterShadow: undefined,
    };
    const validationSchema = Yup.object({
        titleDisplay: Yup.string("Enter title").required("Required"),
        trailer: Yup.string("Enter trailer").required("Required").matches(/^[a-zA-Z0-9_-]{11}$/, "Invalid YouTube video ID"),
        idTMDB: Yup.string("Enter TMDB").required("Required").matches(/^\//, "Invalid ID"),
        rating: Yup.string("Enter rating").required("Required").matches(/^[0-9A-Za-z\-+ ]{1,20}$/, "Invalid characters"),
        posterShadow: Yup.mixed().required("Poster required"),
        // --- TEMPORARY BULK-ENTRY MODE: everything below was required before this push
        // and should go back to required once it's done (see git history for the exact
        // Yup rules -- min(1)-required on genres/directorsGuids/writersGuids, required on
        // synopsis). Left as .optional() here rather than deleted, so reverting is a
        // one-line change per field, not a rewrite. ---
        synopsis: Yup.string("Enter synopsis").optional(),
        // 2026-08-19 (Pascal): genres and tags are back to compulsory even during bulk
        // entry -- Chris assigns these by hand as part of reviewing each TMDB auto-fill,
        // so at least one of each is required before Create title enables.
        genres: Yup.array().min(1, "Choose at least one genre").required("Required"),
        tags: Yup.array().min(1, "Choose at least one tag").required("Required"),
        kind: Yup.string().oneOf(["1", "2"], "Must agree to something").required(),
        // 2026-08-20: Season is what makes each season its own catalog entry, so it's
        // compulsory in Series mode specifically -- conditional on `kind` via .when(),
        // same pattern as every other kind-dependent field on this form.
        season: Yup.string().when('kind', {
            is: '2',
            then: (schema) => schema.required('Required'),
            otherwise: (schema) => schema.optional(),
        }),
        directorsGuids: Yup.array().optional(),
        writersGuids: Yup.array().optional(),
        // NOTE: Yup's .matches() rejects an empty string by default even on an
        // .optional() field -- {excludeEmptyString: true} is required on every
        // optional+matches() pairing below, or a field left blank (no visible input,
        // or TMDB simply not returning that piece of data) permanently fails
        // validation and silently blocks Create title. Bit us for real on idIMDB
        // 2026-08-19 -- fixed here across the board, not just that one field.
        released: Yup.string("Enter year of release").optional().matches(/^(19|20)\d{2}$/, { message: "Invalid year", excludeEmptyString: true }),
        runtime: Yup.string("Enter runtime").optional().matches(/^[0-9hmHM ]{1,20}$/, { message: "Invalid format", excludeEmptyString: true }),
        runtimeHH: Yup.number("Enter runtime HH").optional().min(0).max(23).typeError('HH must be a number'),
        runtimeMM: Yup.number("Enter runtime MM").optional().min(0).max(59).typeError('MM must be a number'),
        // tt\d{7} was the classic fixed-width IMDb id format. IMDb has issued longer,
        // 8+ digit ids since running out of 7-digit ones (~2019) -- a recent title like
        // The Odyssey (2026) legitimately has one of these longer ids, so a strict
        // 7-digit-only regex was rejecting a real, correctly-formatted TMDB-sourced
        // value as "Invalid ID" and permanently blocking Create title. Confirmed this
        // was the actual live blocker on 2026-08-19, separate from the empty-string bug
        // fixed above.
        idIMDB: Yup.string("Enter IMDB").optional().matches(/^tt\d{7,}$/, { message: "Invalid ID", excludeEmptyString: true }),
        idJW: Yup.string("Enter Just Watch").optional().matches(/^\//, { message: "Invalid ID", excludeEmptyString: true }),
        idRT: Yup.string("Enter Rotten Tomatoes").optional().matches(/^\//, { message: "Invalid ID", excludeEmptyString: true }),
        idWiki: Yup.string("Enter Wikipedia").optional(),
        country: Yup.string("Enter country").optional().matches(/^[a-zA-Z]{2}$/, { message: "Invalid country", excludeEmptyString: true }),
        language: Yup.string("Enter language").optional().matches(/^[a-zA-Z]{2}$/, { message: "Invalid language", excludeEmptyString: true }),
        numEpisodes: Yup.number("Enter number of episodes").optional().min(0).max(1000).typeError('Number of episodes must be a number'),
        channelBlackphlix: Yup.boolean(),
        channelFindflix: Yup.boolean(),
    }).test('at-least-one-channel', 'Choose at least one channel', (values) =>
        Boolean(values.channelBlackphlix) || Boolean(values.channelFindflix)
    );

    // FORM ACTION

    async function handleSubmit(form) {
        // get ready in the following state
        setErrorMessage(undefined);
        setModalSpinnerOpen(true);

        // 2026-09-03 (URGENT -- Create title stuck on "Saving record..." forever
        // for a specific, growing set of titles, ~2 weeks): same anti-pattern
        // already fixed on Edit in PR #20 -- this whole save path had no try/catch
        // anywhere, and nothing ever closed the spinner except a fully successful
        // save reaching navigate(). Any uncaught exception mid-save (including the
        // real bug fixed below) left the modal spinning forever with zero error
        // shown. Wrapping the whole thing the same way: a real failure now shows
        // an ErrorBanner and closes the spinner instead of hanging silently.
        try {
            await handleSubmitInner(form);
        } catch (error) {
            console.error('[TitleAddPage] Save failed:', error);
            // 2026-09-03 (URGENT, actively diagnosing the stuck-spinner bug): showing
            // the real error.code/message temporarily instead of a friendly generic
            // banner -- we need the actual text on screen to pin this down, same as
            // the "Needs: ..." diagnostic that cracked the Create-button mystery
            // earlier. Revert to a friendlier message once the real cause is fixed.
            setErrorMessage(`Save failed: [${error.code || 'no code'}] ${error.message || String(error)}`);
            setModalSpinnerOpen(false);
        }
    }

    async function handleSubmitInner(form) {
        // get reference to collection with required fields
        const titlesCollection = collection(firestore, 'titles');
        var documentFields = {
            creator: auth.currentUser.uid,
            created: serverTimestamp(),
            title: form.titleDisplay,
            title_sort: form.titleDisplay,
            trailer: form.trailer,
            genres: form.genres,
            tags: form.tags,
            kind: parseInt(form.kind),
            channels: [
                ...(form.channelBlackphlix ? [1] : []),
                ...(form.channelFindflix ? [2] : []),
            ]
        };

        // 2026-08-19: synopsis is optional during bulk-entry mode (see validationSchema),
        // so unlike before it can genuinely be undefined here -- Firestore's addDoc()
        // throws on an undefined field value, so this must stay conditional now.
        if (form.synopsis !== undefined) {
            documentFields.synopsis = form.synopsis;
        }

        // 2026-08-20: Season, required in Series mode (see validationSchema). Not
        // meaningful for movies, so only written when actually set.
        if (form.season !== undefined && form.season !== '') {
            const seasonInt = parseInt(form.season);
            if (!isNaN(seasonInt)) {
                documentFields.season = seasonInt;
            }
        }

        // 2026-08-29 (Chris, urgent): "Limited Series" is a pure label override --
        // it stores the same season=1 as a plain Season 1 underneath (TMDB has no
        // season-level concept of it), so this is the only thing that distinguishes
        // the two. Only ever written when true, same sparse-field convention as the
        // rest of this schema -- absent (not false) otherwise, and never touches the
        // 5 legacy kind:2 docs or any title already published, since this is Add-only
        // and nothing here retroactively updates existing documents.
        if (form.isLimitedSeries) {
            documentFields.isLimitedSeries = true;
        }

        // add if valid
        if (form.released !== undefined && parseDateString(form.released) instanceof Date) {
            documentFields.release_date = parseDateString(form.released);
        }

        // add if valid
        if (form.runtimeHH !== undefined && form.runtimeMM !== undefined && !isNaN(form.runtimeHH) && !isNaN(form.runtimeMM)) {
            documentFields.runtimeHH = parseInt(form.runtimeHH);
            documentFields.runtimeMM = parseInt(form.runtimeMM);
        }

        // add if valid
        if (form.rating !== undefined) {
            documentFields.rating = form.rating;
        }

        // add num episodes if valid
        if (form.numEpisodes !== undefined) {
            let intVal = parseInt(form.numEpisodes);
            if (!isNaN(intVal)) {
                documentFields.numEpisodes = intVal;
            }
        }

        // add site IDs if valid
        const siteIds = {};
        // truthy check (not just !== undefined): idIMDB has a visible input as of
        // 2026-08-19, so it can legitimately be '' -- don't store an empty id.
        if (form.idIMDB) {
            siteIds['imdb'] = form.idIMDB;
        }
        // truthy for the same reason as idIMDB above -- visible input, can be ''
        if (form.idJW) {
            siteIds['jw'] = form.idJW;
        }
        if (form.idRT !== undefined) {
            siteIds['rt'] = form.idRT;
        }
        if (form.idTMDB !== undefined) {
            siteIds['tmdb'] = form.idTMDB;
        }
        if (form.idWiki !== undefined) {
            siteIds['wiki'] = form.idWiki;
        }

        if (Object.keys(siteIds).length > 0) {
            documentFields.ids = siteIds;
        }

        // add Directors if valid
        if (selectedDirectors !== undefined && Array.isArray(selectedDirectors) && selectedDirectors.length > 0) {
            var directors = {};
            var directorsIdx = [];

            selectedDirectors.forEach(item => {
                directors[item.guid] = item.name;
                directorsIdx.push(item.guid);
            });

            documentFields.directors = directors;
            documentFields.directorsIdx = directorsIdx;
        }

        // add Writers if valid
        if (selectedWriters !== undefined && Array.isArray(selectedWriters) && selectedWriters.length > 0) {
            var writers = {};
            var writersIdx = [];

            selectedWriters.forEach(item => {
                writers[item.guid] = item.name;
                writersIdx.push(item.guid);
            });

            documentFields.writers = writers;
            documentFields.writersIdx = writersIdx;
        }

        // add Cinematographers if valid
        if (selectedCinematographers !== undefined && Array.isArray(selectedCinematographers) && selectedCinematographers.length > 0) {
            var cinematographers = {};
            var cinematographersIdx = [];

            selectedCinematographers.forEach(item => {
                cinematographers[item.guid] = item.name;
                cinematographersIdx.push(item.guid);
            });

            documentFields.cinematographers = cinematographers;
            documentFields.cinematographersIdx = cinematographersIdx;
        }

        // add Editors if valid
        if (selectedEditors !== undefined && Array.isArray(selectedEditors) && selectedEditors.length > 0) {
            var editors = {};
            var editorsIdx = [];

            selectedEditors.forEach(item => {
                editors[item.guid] = item.name;
                editorsIdx.push(item.guid);
            });

            documentFields.editors = editors;
            documentFields.editorsIdx = editorsIdx;
        }

        // add Composers if valid
        if (selectedComposers !== undefined && Array.isArray(selectedComposers) && selectedComposers.length > 0) {
            var composers = {};
            var composersIdx = [];

            selectedComposers.forEach(item => {
                composers[item.guid] = item.name;
                composersIdx.push(item.guid);
            });

            documentFields.composers = composers;
            documentFields.composersIdx = composersIdx;
        }

        // 2026-08-20 (Pascal): Creator(s) and Showrunner(s) merged into one Portal
        // field/list (selectedCreators) -- both apps only ever render `creators`
        // (confirmed: no `showrunners` reference anywhere in FindFlix-iOS or
        // FindFlix-Android), so a separate Showrunner(s) control was populating a
        // field nobody could actually see. Still written to `showrunners`/
        // `showrunnersIdx` too, mirrored from the same list, purely for backward
        // compatibility with the schema in case something reads it later -- there is
        // now exactly one set of people behind both fields.
        if (selectedCreators !== undefined && Array.isArray(selectedCreators) && selectedCreators.length > 0) {
            var creators = [];
            var creatorsIdx = [];

            selectedCreators.forEach(item => {
                creators.push(item);
                creatorsIdx.push(item.guid);
            });

            // Also fixes a pre-existing bug found in passing: this previously read
            // `documentFields.creatorsIdx = creators` (the plain list again, not the
            // guid-only index array actually built above) -- same class of bug as the
            // directorsIdx-never-written issue fixed on Edit in PR #18.
            documentFields.creators = creators;
            documentFields.creatorsIdx = creatorsIdx;
            documentFields.showrunners = creators;
            documentFields.showrunnersIdx = creatorsIdx;
        }

        // add Producers if valid
        if (selectedProducers !== undefined && Array.isArray(selectedProducers) && selectedProducers.length > 0) {
            var producers = [];
            var producersIdx = [];

            selectedProducers.forEach(item => {
                producers.push(item);
                producersIdx.push(item.guid);
            });

            documentFields.producers = producers;
            documentFields.producersIdx = producersIdx;
        }

        // add Cast if valid
        if (selectedCast !== undefined && Array.isArray(selectedCast) && selectedCast.length > 0) {
            var cast = [];
            var castIdx = [];

            selectedCast.forEach(item => {
                cast.push(item);
                castIdx.push(item.guid);
            });

            documentFields.cast = cast;
            documentFields.castIdx = castIdx;
        }

        // add Watch if valid
        if (selectedWatch !== undefined && Array.isArray(selectedWatch) && selectedWatch.length > 0) {
            var watchItems = [];

            selectedWatch.forEach(item => {
                watchItems.push(item);
            });

            documentFields.watch = watchItems;
        }

        // add Country if valid
        if (form.country !== undefined && form.country.length === 2) {
            documentFields.country = [form.country];
        }

        // add Language if valid
        if (form.language !== undefined && form.language.length === 2) {
            documentFields.language = form.language;
        }

        // Debug:
        // console.log(JSON.stringify(documentFields));
        // window.alert(JSON.stringify(documentFields)); return;

        setModalMessage('Saving record...');

        // 2026-09-03 (URGENT, leading hypothesis for the actual "stuck forever"
        // cause): every one of the ~70 titles Chris flagged as failing shares one
        // thing that's easy to miss -- they're all genuinely obscure, hard-to-
        // research titles (Satyajit Ray's filmography, 1970s British sitcoms,
        // foreign-language classics), unlike the routine bulk titles that were
        // still saving fine every 60-320s right up until this started. Firebase ID
        // tokens expire hourly and are refreshed by a background timer -- which
        // Chrome (and other browsers) THROTTLE in backgrounded tabs. Researching
        // one of these titles properly means tabbing away to TMDB/Wikipedia/Google
        // for a while, exactly the condition under which that refresh timer can
        // fall behind. A write attempted on a token that went stale while the tab
        // sat in the background can hang rather than fail cleanly. Forcing a fresh
        // token immediately before the write costs one extra round-trip and
        // sidesteps this regardless of whether this exact mechanism turns out to
        // be the full explanation.
        await auth.currentUser.getIdToken(true);

        // PART A: save the record
        // 2026-09-03: this used to catch the error here, setModalSpinnerOpen(false)
        // + setErrorMessage(...), and then fall through to `docRef.id` below with
        // docRef now undefined (the catch callback returns nothing) -- a SECOND,
        // uncaught TypeError on every single save failure, silently swallowed as
        // an unhandled promise rejection. Removed the inline catch entirely; a
        // real failure now throws once, cleanly, and is caught by the outer
        // try/catch in handleSubmit.
        const docRef = await addDoc(titlesCollection, documentFields);
        console.log("Added title " + docRef.id);

        // PART B: upload the photo
        // 2026-09-03: same silent-swallow anti-pattern as PART A above (and the one
        // already found and fixed on Edit's poster-replace path) -- this caught the
        // upload error, logged it, and then unconditionally proceeded to updateDoc
        // regardless, pointing the doc at a Storage object that may never have been
        // written. Not the cause of the stuck-spinner bug (this only runs after
        // addDoc already succeeded), but the same class of bug, fixed the same way:
        // let a real failure throw and be caught by the outer try/catch, rather
        // than silently continuing as if it succeeded. Unlike Edit's poster-replace
        // fix, this path doesn't need a timestamped filename -- docRef.id is a
        // brand-new id from the addDoc() just above, so thumbs/<docRef.id>.png has
        // never been uploaded to (or cached) before now; the CDN-caching problem
        // only bites on a REPLACE of an existing path, which Add never does.
        let docIdRegex = /^[A-Za-z0-9]{10,30}$/;
        if (artworkImgFile !== undefined && docIdRegex.test(docRef.id)) {
            setModalMessage('Uploading cover...');
            const storageRef = ref(storage, `thumbs/${docRef.id}.png`);
            await uploadBytes(storageRef, artworkImgFile);
            console.log('Uploaded cover artwork');
            await updateDoc(docRef, { thumbnail: `https://cdn.findflix.com/thumbs/${docRef.id}.png` });
        }

        // outta here
        navigate('/app');
    }

    // HELPERS

    // 2026-08-19: Year of Release only collects a 4-digit year -- stored as a Timestamp
    // (still needed for existing sort/range-query code across the client apps) using a
    // mid-year date, not Jan 1. Dates are constructed in the browser's local timezone,
    // then stored as UTC -- Jan 1 is the one day of the year a negative timezone offset
    // can roll into the *previous* year, so someone viewing from a different timezone
    // than whoever entered it could see the wrong year. July 1 at noon has ~6 months of
    // buffer either way, far more than any real timezone spread (max ~26h), so the
    // displayed year is always correct regardless of who's viewing it from where.
    const parseDateString = (str) => {
        const year = Number(str);
        const parsedDate = new Date(year, 6, 1, 12, 0, 0);
        return parsedDate;
    }

    const clearDirectorPressed = async (director, setFieldValue, setFieldTouched) => {
        const directorsClone = cloneDeep(selectedDirectors);

        // console.log(director);

        var removeIndex = directorsClone.map(item => item.guid).indexOf(director.guid);
        (removeIndex >= 0) && directorsClone.splice(removeIndex, 1);

        await setSelectedDirectors(directorsClone);
        await setFieldValue('directorsGuids', directorsClone.map(item => item.guid));
        await setFieldTouched('directorsGuids', true, true);
    }

    const clearWriterPressed = async (writer, setFieldValue, setFieldTouched) => {
        const writersClone = cloneDeep(selectedWriters);

        var removeIndex = writersClone.map(item => item.guid).indexOf(writer.guid);
        (removeIndex >= 0) && writersClone.splice(removeIndex, 1);

        await setSelectedWriters(writersClone);
        await setFieldValue('writersGuids', writersClone.map(item => item.guid));
        await setFieldTouched('writersGuids', true, true);
    }

    const clearCinematographerPressed = async (cinematographer, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedCinematographers);

        var removeIndex = clone.map(item => item.guid).indexOf(cinematographer.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedCinematographers(clone);
    }

    const clearEditorPressed = async (editor, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedEditors);

        var removeIndex = clone.map(item => item.guid).indexOf(editor.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedEditors(clone);
    }

    const clearComposerPressed = async (composer, setFieldValue, setFieldTouched) => {
        const clone = cloneDeep(selectedComposers);

        var removeIndex = clone.map(item => item.guid).indexOf(composer.guid);
        (removeIndex >= 0) && clone.splice(removeIndex, 1);

        await setSelectedComposers(clone);
    }

    const clearCreatorPressed = async (creator, setFieldValue, setFieldTouched) => {
        const creatorsClone = cloneDeep(selectedCreators);

        var removeIndex = creatorsClone.map(item => item.guid).indexOf(creator.guid);
        (removeIndex >= 0) && creatorsClone.splice(removeIndex, 1);

        await setSelectedCreators(creatorsClone);
    }

    const clearProducerPressed = async (producer, setFieldValue, setFieldTouched) => {
        const producersClone = cloneDeep(selectedProducers);

        var removeIndex = producersClone.map(item => item.guid).indexOf(producer.guid);
        (removeIndex >= 0) && producersClone.splice(removeIndex, 1);

        await setSelectedProducers(producersClone);
    }

    const clearCastPressed = async (actor, setFieldValue, setFieldTouched) => {
        const castClone = cloneDeep(selectedCast);

        var removeIndex = castClone.map(item => item.guid).indexOf(actor.guid);
        (removeIndex >= 0) && castClone.splice(removeIndex, 1);

        await setSelectedCast(castClone);
    }

    const clearWatchPressed = async (watchItem, setFieldValue, setFieldTouched) => {
        const watchClone = cloneDeep(selectedWatch);

        var removeIndex = watchClone.map(item => item.url).indexOf(watchItem.url);
        (removeIndex >= 0) && watchClone.splice(removeIndex, 1);

        await setSelectedWatch(watchClone);
    }

    const showAddActor = (e) => {
        e.stopPropagation();

        setModalActorOpen(true);
    }

    const showAddWatch = (e) => {
        e.stopPropagation();

        setModalWatchOpen(true);
    }

    const cancelPressed = () => {
        navigate('/app');
    }

    // from DirectorAutocomplete
    // from DirectorAutocomplete
    const handleCreatorChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        // check if already in list
        if (selectedCreators.some(element => {
            if (element.guid === newVal.id) {
                return true;
            }

            return false;
        })) {
            return;
        }

        // keep our own copy outside the form to display to the user
        await setSelectedCreators([{ guid: newVal.id, name: newVal.name }, ...selectedCreators]);
    }

    // from DirectorAutocomplete
    const handleDirectorChange = async (newVal, setFieldValue, setFieldTouched) => {
        console.log("handleDirectorChange " + JSON.stringify(newVal));

        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedDirectors.some(element => {
            if (element.guid === newVal.id) {
                return true;
            }

            return false;
        })) {
            return;
        }

        // keep our own copy outside the form to display to the user
        const newDirectors = [{ guid: newVal.id, name: newVal.name }, ...selectedDirectors];
        await setSelectedDirectors(newDirectors);
        await setFieldValue('directorsGuids', newDirectors.map(item => item.guid));
        await setFieldTouched('directorsGuids', true, true);
    }

    // from DirectorAutocomplete -- Writer reuses the same directors lookup/create flow
    const handleWriterChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedWriters.some(element => element.guid === newVal.id)) {
            return;
        }

        const newWriters = [{ guid: newVal.id, name: newVal.name }, ...selectedWriters];
        await setSelectedWriters(newWriters);
        await setFieldValue('writersGuids', newWriters.map(item => item.guid));
        await setFieldTouched('writersGuids', true, true);
    }

    // from DirectorAutocomplete -- optional, no Formik validation wiring needed
    const handleCinematographerChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedCinematographers.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedCinematographers([{ guid: newVal.id, name: newVal.name }, ...selectedCinematographers]);
    }

    const handleEditorChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedEditors.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedEditors([{ guid: newVal.id, name: newVal.name }, ...selectedEditors]);
    }

    const handleComposerChange = async (newVal, setFieldValue, setFieldTouched) => {
        if (newVal === undefined || !newVal.id || !newVal.name) {
            return;
        }

        if (selectedComposers.some(element => element.guid === newVal.id)) {
            return;
        }

        await setSelectedComposers([{ guid: newVal.id, name: newVal.name }, ...selectedComposers]);
    }

    // 2026-08-19: added a reject path + timeout. The original version only ever called
    // resolve(), never reject() -- if Resizer's callback never fired for some reason
    // (found via a real TMDB auto-fill test: the resulting `await resizeFile(...)` just
    // hung forever, silently blocking everything after it, including the posterShadow
    // flag that unlocks Save -- poster preview still showed since that's set earlier,
    // independent of this call, which is what made it confusing). Now callers can catch
    // a failure instead of hanging indefinitely.
    const resizeFile = (file) =>
        new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('resizeFile timed out')), 10000);
            try {
                Resizer.imageFileResizer(file, 200, 300, "PNG", 95, 0,
                    (uri) => {
                        clearTimeout(timeout);
                        resolve(uri);
                    },
                    "file"
                );
            } catch (e) {
                clearTimeout(timeout);
                reject(e);
            }
        });

    const handleArtworkImageUpload = (e, setFieldValue) => {
        const [file] = e.target.files;
        if (file) {
            const reader = new FileReader();
            artworkImgRef.current.file = file;
            reader.onload = async (e) => {
                artworkImgRef.current.src = e.target.result;
                const deprefixed = e.target.result.replace(/^(data:image\/png;base64,)/, ''); // data:image/png;base64,
                setArtworkImgEncoded(deprefixed);
                // 2026-08-19: satisfies the (temporary, bulk-entry-mode) required-poster
                // check as soon as we have a file, not gated on resize succeeding below
                // (resizeFile can fail/time out -- see its own comment) -- see
                // posterShadow in validationSchema.
                setFieldValue('posterShadow', true);
                setArtworkImgFile(file); // fallback if resize below fails/times out
                try {
                    const resizedImage = await resizeFile(file);
                    setArtworkImgFile(resizedImage);
                } catch (resizeError) {
                    console.warn('[handleArtworkImageUpload] resize failed, uploading original image instead:', resizeError);
                }
            }
            reader.readAsDataURL(file);
        }
    };

    const notRatedChanged = (e, setFieldValue, setFieldTouched) => {
        if (e !== undefined && e.target.checked) {
            setFieldValue('rating', "Not rated");
            setFieldTouched('rating', true);
            ratingFieldRef.current.value = "Not rated";
            ratingFieldRef.current.disabled = true;
        } else {
            setFieldValue('rating', undefined);
            setFieldTouched('rating', true);
            ratingFieldRef.current.value = "";
            ratingFieldRef.current.disabled = false;
        }
    }

    // 2026-08-19: duplicate-title check, run on blur of either the Display title or
    // Year of Release fields so it re-evaluates however the admin fills the form in
    // (title first then year, or vice versa). Reads the title straight off the input
    // ref rather than tracking extra state, since the field is uncontrolled.
    const runDuplicateCheck = (yearStr) => {
        const titleValue = titleDisplayRef.current ? titleDisplayRef.current.value : undefined;
        const yearValue = (yearStr !== undefined && /^(19|20)\d{2}$/.test(yearStr)) ? parseInt(yearStr) : undefined;
        setDuplicateWarnings(findSimilarTitles(titleValue, yearValue, allTitlesLite || []));
    }

    const titleDisplayBlurred = (e, handleBlur, values) => {
        handleBlur(e);
        runDuplicateCheck(values.released);
    }

    const releasedBlurred = (e, handleBlur) => {
        handleBlur(e);
        runDuplicateCheck(e.target.value);
    }

    // 2026-08-19: lets a full YouTube/TMDB URL be pasted directly into these fields --
    // extracts just the ID/path and overwrites both the visible input text and Formik's
    // value with it. No-op if what's pasted/typed doesn't look like a URL (see
    // urlParsing.js), so typing a bare ID directly still works exactly as before.
    const handleTrailerFieldChange = (e, setFieldValue) => {
        const extracted = extractYoutubeId(e.target.value);
        e.target.value = extracted;
        setFieldValue('trailer', extracted);
    }

    // 2026-08-29 (Chris, urgent): human-readable label for the Season selection, used
    // to build the Display title ("<series> — <label>") in populateFromTmdbTv.
    // Limited Series overrides everything (it's a pure label choice, storage-wise
    // it's just season=1 like a plain Season 1); otherwise Specials for season 0,
    // else "Season N".
    const computeSeasonLabel = (seasonNum, isLimited) => {
        if (isLimited) return 'Limited Series';
        if (seasonNum === 0) return 'Specials';
        return `Season ${seasonNum}`;
    }

    // 2026-08-20: kind-aware -- Series mode parses/fetches against TMDB's TV
    // endpoints instead of movie ones, but still writes into the same idTMDB field
    // (and siteIds.tmdb on submit). `values` is threaded through so this can check
    // whether a Season is already chosen -- if so, changing the URL re-fetches
    // immediately rather than waiting on the Season field to also change.
    //
    // 2026-08-20 (bug found in Chris's testing): pasting a season-specific TMDB URL
    // (.../tv/1396-breaking-bad/season/3) was silently stripped down to just the
    // series path before being stored -- meaning every season of the same show ended
    // up with an IDENTICAL siteIds.tmdb value, so the app's "View on TMDB" link
    // (built by concatenating that stored path onto themoviedb.org) sent every season
    // to the show's main page instead of its own season page. Fixed by keeping the
    // /season/N suffix on the STORED/displayed idTMDB path (so each season's TMDB
    // link is genuinely distinct) while still passing only the bare series path to
    // fetchTmdbTvSeasonDetails/the watch-link builder, which need the unsuffixed path
    // to hit the right endpoints. Also auto-selects the Season dropdown when the
    // pasted URL is season-specific, so one paste now does the whole job.
    // 2026-08-29 (Chris, urgent): season 0 ("Specials" on TMDB) is a legitimate,
    // real season and is FALSY in JS -- every truthy check on a season number below
    // (`effectiveSeason &&`, `seasonNum &&`, etc.) would silently treat Specials as
    // "no season chosen" and refuse to fetch. Every season-number check in this file
    // is now explicit (`!== undefined`), not a truthy shorthand -- audited the whole
    // TV series feature for this after adding Specials support.
    const handleTmdbFieldChange = (e, setFieldValue, setFieldTouched, values) => {
        if (useSeriesMetadata) {
            const seriesPath = extractTmdbTvPath(e.target.value);
            const pastedSeason = extractTmdbSeasonNumber(e.target.value);
            const currentSeason = (values.season !== undefined && values.season !== '') ? parseInt(values.season) : undefined;
            const effectiveSeason = pastedSeason !== undefined ? pastedSeason : currentSeason;

            const displayPath = effectiveSeason !== undefined ? `${seriesPath}/season/${effectiveSeason}` : seriesPath;
            e.target.value = displayPath;
            setFieldValue('idTMDB', displayPath);

            // A pasted URL can only ever resolve to a real TMDB season number (0-99)
            // -- season 0 maps to the "Specials" dropdown entry, everything else to
            // its plain numbered entry. There's no URL that means "Limited Series"
            // (TMDB has no such concept -- it's Season 1 under the hood either way),
            // so pasting always clears that flag; picking "Limited Series" from the
            // dropdown afterward is the only way to set it. If nothing was pasted
            // (plain series URL), carry over whatever season/label was already chosen.
            const isLimited = pastedSeason !== undefined ? false : Boolean(values.isLimitedSeries);
            if (pastedSeason !== undefined) {
                setFieldValue('season', String(pastedSeason));
                setFieldValue('seasonSelection', pastedSeason === 0 ? 'specials' : String(pastedSeason));
                setFieldValue('isLimitedSeries', false);
            }

            if (/^\/tv\/\d+/.test(seriesPath) && effectiveSeason !== undefined) {
                const seasonLabel = computeSeasonLabel(effectiveSeason, isLimited);
                populateFromTmdbTv(seriesPath, effectiveSeason, seasonLabel, setFieldValue, setFieldTouched);
            }
            return;
        }

        const extracted = extractTmdbPath(e.target.value);
        e.target.value = extracted;
        setFieldValue('idTMDB', extracted);

        if (/^\/movie\/\d+/.test(extracted)) {
            populateFromTmdb(extracted, setFieldValue, setFieldTouched);
        }
    }

    // 2026-08-20: the Season dropdown's onChange -- mirrors handleTmdbFieldChange's
    // logic in reverse: if a valid TV URL is already in the idTMDB field, choosing
    // (or changing) the season re-fetches immediately, and rewrites the displayed/
    // stored idTMDB path to include the newly chosen season (see the bug note above
    // handleTmdbFieldChange -- this keeps the two entry points, paste vs. dropdown,
    // consistent with each other).
    //
    // 2026-08-29 (Chris, urgent): the dropdown's own value (`selection`) is now one of
    // '', 'limited', 'specials', or a plain '1'-'99' string -- translated here into
    // the actual `season` number (0 for Specials, 1 for Limited Series, otherwise
    // itself) plus `isLimitedSeries`. `season` and `isLimitedSeries` are kept separate
    // because Limited Series and a plain Season 1 both store season=1 -- the select
    // needs its OWN state (seasonSelection) to show the right option as selected,
    // since two different selections can map to the same underlying season number.
    const handleSeasonFieldChange = (e, setFieldValue, setFieldTouched, values) => {
        const selection = e.target.value;
        setFieldValue('seasonSelection', selection);

        let seasonNum;
        let isLimited = false;
        if (selection === 'specials') {
            seasonNum = 0;
        } else if (selection === 'limited') {
            seasonNum = 1;
            isLimited = true;
        } else if (selection !== '') {
            seasonNum = parseInt(selection);
        }

        setFieldValue('season', seasonNum !== undefined ? String(seasonNum) : '');
        setFieldValue('isLimitedSeries', isLimited);

        if (seasonNum !== undefined && values.idTMDB && /^\/tv\/\d+/.test(values.idTMDB)) {
            const seriesPath = extractTmdbTvPath(values.idTMDB); // strips any /season/N already there
            const displayPath = `${seriesPath}/season/${seasonNum}`;
            if (idTMDBRef.current) idTMDBRef.current.value = displayPath;
            setFieldValue('idTMDB', displayPath);
            populateFromTmdbTv(seriesPath, seasonNum, computeSeasonLabel(seasonNum, isLimited), setFieldValue, setFieldTouched);
        }
    }

    // 2026-08-19: IMDB id now has a visible field (auto-filled from TMDB, manually
    // correctable). Accepts a full imdb.com/title/... URL paste, same as trailer/TMDB.
    const handleImdbFieldChange = (e, setFieldValue) => {
        const extracted = extractImdbId(e.target.value);
        e.target.value = extracted;
        setFieldValue('idIMDB', extracted);
    }

    // NOTE on JustWatch (2026-08-19): a visible idJW field existed briefly, then was
    // removed per Pascal -- Chris shouldn't need to look titles up on a second site.
    // "Where to watch" will instead be an app-side change: link to TMDB's watch page,
    // derived from the already-stored siteIds.tmdb ("/movie/<id>-<slug>" + "/watch").
    // siteIds.jw remains supported for titles that already have it (both apps render
    // a JustWatch link from it), it just isn't settable from this form.

    // 2026-08-19: live TMDB auto-fill, part of TEMPORARY BULK-ENTRY MODE (Chris x Pascal,
    // 2026-08-19). Fires once a valid TMDB movie path is entered/pasted. Fetches
    // synopsis/runtime/year/country/language/director/writer/cast/poster and populates
    // the form so Chris can review before saving rather than typing all of it by hand.
    // Genres/Tags are deliberately NOT touched -- Chris does those manually either way.
    // Movies only -- series lookup is a phase-two follow-up.
    const populateFromTmdb = async (tmdbPath, setFieldValue, setFieldTouched) => {
        setTmdbFetchStatus('loading');
        setTmdbFetchNote(undefined);

        const data = await fetchTmdbMovieDetails(tmdbPath);

        if (data.error) {
            setTmdbFetchStatus('error');
            setTmdbFetchNote(
                data.error === 'no_api_key' ? 'TMDB API key not configured -- ask Pascal.' :
                    data.error === 'not_found' ? 'No TMDB movie found for this ID.' :
                        'Could not reach TMDB -- check the ID and try again.'
            );
            return;
        }

        // 2026-08-19: pull Display title too, per Pascal -- lets Chris paste the TMDB
        // link first and have the title itself fill in, rather than needing to type it
        // before he even has the TMDB page open. Still just a pre-fill -- editable like
        // every other auto-filled field. Re-runs the duplicate check against the real
        // title once it lands, in case Chris never touches this field directly himself
        // (which the blur-triggered check alone wouldn't catch).
        if (data.tmdbTitle) {
            if (titleDisplayRef.current) {
                titleDisplayRef.current.value = data.tmdbTitle;
            }
            setFieldValue('titleDisplay', data.tmdbTitle);
            setFieldTouched('titleDisplay', true);
            runDuplicateCheck(data.releaseYear !== undefined ? String(data.releaseYear) : undefined);
        }

        if (data.overview) {
            if (synopsisRef.current) {
                synopsisRef.current.value = data.overview;
            }
            setFieldValue('synopsis', data.overview);
        }

        if (data.runtimeMinutes !== undefined) {
            const hh = Math.floor(data.runtimeMinutes / 60);
            const mm = data.runtimeMinutes % 60;
            if (runtimeHHRef.current) runtimeHHRef.current.value = hh;
            if (runtimeMMRef.current) runtimeMMRef.current.value = mm;
            setFieldValue('runtimeHH', hh);
            setFieldValue('runtimeMM', mm);
        }

        if (data.releaseYear !== undefined) {
            const yearStr = String(data.releaseYear);
            if (releasedRef.current) releasedRef.current.value = yearStr;
            setFieldValue('released', yearStr);
            runDuplicateCheck(yearStr);
        }

        // idIMDB has a visible input as of 2026-08-19 (uncontrolled, so update the DOM
        // value via ref alongside Formik). Saved into the title's siteIds.imdb metadata
        // on submit, which is what the app reads to show the clickable IMDB link.
        if (data.imdbId) {
            if (idIMDBRef.current) {
                idIMDBRef.current.value = data.imdbId;
            }
            setFieldValue('idIMDB', data.imdbId);
        }

        if (data.countryCode) {
            setFieldValue('country', data.countryCode);
            setFieldTouched('country', true);
        }
        if (data.languageCode) {
            setFieldValue('language', data.languageCode);
            setFieldTouched('language', true);
        }

        // 2026-08-19 (Pascal): auto-add a "Where to watch" entry pointing at TMDB's
        // watch page for this movie. The iOS app already renders title.watch entries,
        // so this ships the where-to-watch feature with zero app changes and zero
        // work for Chris. TMDB's watch page shows JustWatch-licensed availability
        // with a country picker, so it stays current for every visitor -- unlike
        // storing provider lists, which would snapshot one country and go stale.
        // Functional update + themoviedb.org filter so re-pasting a different TMDB
        // link replaces the auto-entry rather than stacking duplicates, while any
        // manually-added entries are preserved.
        setSelectedWatch(prev => [
            { title: 'Where to watch (TMDB)', url: `https://www.themoviedb.org${tmdbPath}/watch` },
            ...prev.filter(w => !(w.url && w.url.includes('themoviedb.org'))),
        ]);

        // Directors and Writers share the `directors` people-lookup collection.
        if (data.directors.length > 0) {
            const directorPeople = await findOrCreatePeople(firestore, auth, 'directors', data.directors);
            setSelectedDirectors(directorPeople);
            setFieldValue('directorsGuids', directorPeople.map(p => p.guid));
        }
        if (data.writers.length > 0) {
            const writerPeople = await findOrCreatePeople(firestore, auth, 'directors', data.writers);
            setSelectedWriters(writerPeople);
            setFieldValue('writersGuids', writerPeople.map(p => p.guid));
        }

        // Cinematographer/Editor/Composer also share the `directors` people collection,
        // same as Director/Writer/Producer/Showrunner/Creator.
        if (data.cinematographers.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.cinematographers);
            setSelectedCinematographers(people);
        }
        if (data.editors.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.editors);
            setSelectedEditors(people);
        }
        if (data.composers.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.composers);
            setSelectedComposers(people);
        }

        // Producer(s), capped at 3 (see tmdbService.js). Shares the `directors` people
        // collection like the others above; `title` (Producer/Executive Producer/etc.)
        // comes straight from TMDB's own job title.
        // 2026-09-03 (URGENT fix, root cause of the stuck-spinner bug -- see
        // tmdbService.js for the full writeup): two fixes here together --
        //   1. `title`/`role` is now only ever set to a real string or omitted
        //      entirely, never explicitly `undefined` (Firestore's addDoc()
        //      rejects the whole document if ANY value anywhere is undefined,
        //      including nested in an array -- confirmed this was silently
        //      killing "Create title" for any cast member with no character
        //      credit, common on documentaries).
        //   2. matched by name instead of by array index -- findOrCreatePeople
        //      silently skips (doesn't placeholder) a name if its lookup fails,
        //      which would have misaligned index-based pairing between a person
        //      and the wrong person's title/role.
        if (data.producers.length > 0) {
            const producerNames = data.producers.map(p => p.name);
            const producerPeople = await findOrCreatePeople(firestore, auth, 'directors', producerNames);
            const producersWithTitle = producerPeople.map(p => {
                const match = data.producers.find(dp => dp.name === p.name);
                return (match && match.title) ? { ...p, title: match.title } : p;
            });
            setSelectedProducers(producersWithTitle);
        }

        // Cast uses a separate `cast` collection.
        if (data.cast.length > 0) {
            const castNames = data.cast.map(c => c.name);
            const castPeople = await findOrCreatePeople(firestore, auth, 'cast', castNames);
            const castWithRoles = castPeople.map(p => {
                const match = data.cast.find(dc => dc.name === p.name);
                return (match && match.role) ? { ...p, role: match.role } : p;
            });
            setSelectedCast(castWithRoles);
        }

        if (data.posterUrl) {
            try {
                const imgResponse = await fetch(data.posterUrl);
                const imgBlob = await imgResponse.blob();
                if (artworkImgRef.current) {
                    artworkImgRef.current.src = URL.createObjectURL(imgBlob);
                }
                // Satisfy the required-poster check as soon as we have real image bytes
                // -- deliberately NOT waiting on the resize step below, which is a
                // best-effort optimization, not a precondition for "a poster exists".
                setFieldValue('posterShadow', true);
                setArtworkImgFile(imgBlob); // fallback if resize below fails/times out

                try {
                    const resizedImage = await resizeFile(imgBlob);
                    setArtworkImgFile(resizedImage);
                } catch (resizeError) {
                    console.warn('[populateFromTmdb] poster resize failed, uploading original image instead:', resizeError);
                }
            } catch (e) {
                console.warn('[populateFromTmdb] poster fetch failed:', e);
            }
        }

        const gaps = [];
        if (!data.overview) gaps.push('synopsis');
        if (!data.directors.length) gaps.push('director');
        if (!data.writers.length) gaps.push('writer');
        if (!data.posterUrl) gaps.push('poster');

        setTmdbFetchStatus('success');
        setTmdbFetchNote(
            gaps.length > 0
                ? `Pulled "${data.tmdbTitle}" from TMDB, but it's missing: ${gaps.join(', ')} -- fill those in by hand.`
                : `Pulled "${data.tmdbTitle}" from TMDB.`
        );
    }

    // 2026-08-20: TV series auto-fill, mirrors populateFromTmdb above field-for-field
    // where a movie equivalent exists. Differences (see fetchTmdbTvSeasonDetails for
    // the fetch-side reasoning):
    //   - Display title is "<series name> — Season <N>" (confirmed with Pascal
    //     2026-08-20), not just the series name -- each season is its own entry.
    //   - Released/Year of Release uses the SEASON's own air date, not the series'
    //     first-air date -- each season aired in its own year (originally left unset
    //     entirely; added 2026-08-20 after Chris's testing expected it to pull
    //     through like every other field).
    //   - Episode Director(s)/Writer(s) hold the full distinct set aggregated across
    //     the selected season's episodes (TMDB has no single "the director" for a
    //     season the way a movie has one director) -- editable/correctable by Chris
    //     same as everything else.
    //   - Creator(s) auto-fills from TMDB's created_by. Originally built against a
    //     separate Showrunner(s) field per spec (TMDB's created_by maps conceptually
    //     onto either term) -- merged into Creator(s) 2026-08-20 per Pascal, since
    //     only `creators` is ever rendered by either shipped app.
    //   - Number of episodes is set from the season's actual episode count.
    //   - 2026-08-29 (Chris, urgent): `seasonLabel` lets the Display title read
    //     "— Specials" or "— Limited Series" instead of "— Season N" when
    //     applicable (see computeSeasonLabel above) -- built here from
    //     data.seriesName + seasonLabel, since tmdbService.js has no concept of
    //     either and only knows plain season numbers.
    const populateFromTmdbTv = async (tmdbPath, seasonNumber, seasonLabel, setFieldValue, setFieldTouched) => {
        setTmdbFetchStatus('loading');
        setTmdbFetchNote(undefined);

        const data = await fetchTmdbTvSeasonDetails(tmdbPath, seasonNumber);

        if (data.error) {
            setTmdbFetchStatus('error');
            setTmdbFetchNote(
                data.error === 'no_api_key' ? 'TMDB API key not configured -- ask Pascal.' :
                    data.error === 'not_found' ? 'No TMDB series found for this URL.' :
                        data.error === 'season_not_found' ? `This series doesn't appear to have a ${seasonLabel || `Season ${seasonNumber}`}.` :
                            'Could not reach TMDB -- check the URL and try again.'
            );
            return;
        }

        const displayTitle = data.seriesName ? `${data.seriesName} — ${seasonLabel || `Season ${seasonNumber}`}` : undefined;

        if (displayTitle) {
            if (titleDisplayRef.current) {
                titleDisplayRef.current.value = displayTitle;
            }
            setFieldValue('titleDisplay', displayTitle);
            setFieldTouched('titleDisplay', true);
            runDuplicateCheck(data.releaseYear !== undefined ? String(data.releaseYear) : undefined);
        }

        if (data.overview) {
            if (synopsisRef.current) {
                synopsisRef.current.value = data.overview;
            }
            setFieldValue('synopsis', data.overview);
        }

        if (data.numEpisodes !== undefined) {
            if (numEpisodesRef.current) numEpisodesRef.current.value = data.numEpisodes;
            setFieldValue('numEpisodes', data.numEpisodes);
        }

        // 2026-08-20 (added after Chris's testing -- this wasn't set at all originally,
        // see the PR description's "NOT auto-filled" list): the season's own air year,
        // not the series' first-air year -- each season aired in its own year. Also
        // means the duplicate-title check above now gets a real year for TV entries,
        // partially narrowing the "sibling seasons flag each other" noise flagged
        // earlier (still imprecise for seasons that aired only ~1 year apart, since
        // the check's yearsDiffer threshold is >1 year).
        if (data.releaseYear !== undefined) {
            const yearStr = String(data.releaseYear);
            if (releasedRef.current) releasedRef.current.value = yearStr;
            setFieldValue('released', yearStr);
            runDuplicateCheck(yearStr);
        }

        if (data.runtimeMinutes !== undefined) {
            const hh = Math.floor(data.runtimeMinutes / 60);
            const mm = data.runtimeMinutes % 60;
            if (runtimeHHRef.current) runtimeHHRef.current.value = hh;
            if (runtimeMMRef.current) runtimeMMRef.current.value = mm;
            setFieldValue('runtimeHH', hh);
            setFieldValue('runtimeMM', mm);
        }

        if (data.imdbId) {
            if (idIMDBRef.current) {
                idIMDBRef.current.value = data.imdbId;
            }
            setFieldValue('idIMDB', data.imdbId);
        }

        if (data.countryCode) {
            setFieldValue('country', data.countryCode);
            setFieldTouched('country', true);
        }
        if (data.languageCode) {
            setFieldValue('language', data.languageCode);
            setFieldTouched('language', true);
        }

        // Same TMDB watch-page auto-add as movies (PR #17), using the TV watch page
        // URL shape confirmed against the live API 2026-08-20
        // (themoviedb.org/tv/<id>-<slug>/watch).
        setSelectedWatch(prev => [
            { title: 'Where to watch (TMDB)', url: `https://www.themoviedb.org${tmdbPath}/watch` },
            ...prev.filter(w => !(w.url && w.url.includes('themoviedb.org'))),
        ]);

        // data.showrunners is TMDB's created_by -- feeds the merged Creator(s) field
        // (see the comment above this function).
        if (data.showrunners.length > 0) {
            const creatorPeople = await findOrCreatePeople(firestore, auth, 'directors', data.showrunners);
            setSelectedCreators(creatorPeople);
        }

        // Episode Director(s)/Writer(s) -- see the aggregation note in
        // fetchTmdbTvSeasonDetails. Same directorsGuids/writersGuids shadow fields as
        // movies, so validation and the "Needs: ..." diagnostic keep working unchanged.
        if (data.directors.length > 0) {
            const directorPeople = await findOrCreatePeople(firestore, auth, 'directors', data.directors);
            setSelectedDirectors(directorPeople);
            setFieldValue('directorsGuids', directorPeople.map(p => p.guid));
        }
        if (data.writers.length > 0) {
            const writerPeople = await findOrCreatePeople(firestore, auth, 'directors', data.writers);
            setSelectedWriters(writerPeople);
            setFieldValue('writersGuids', writerPeople.map(p => p.guid));
        }

        if (data.cinematographers.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.cinematographers);
            setSelectedCinematographers(people);
        }
        if (data.editors.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.editors);
            setSelectedEditors(people);
        }
        if (data.composers.length > 0) {
            const people = await findOrCreatePeople(firestore, auth, 'directors', data.composers);
            setSelectedComposers(people);
        }

        // 2026-09-03: same urgent fix as the movie flow above -- never set title/role
        // to explicit undefined (Firestore rejects the whole document), and match
        // by name rather than array index (robust against findOrCreatePeople
        // skipping a failed lookup). See tmdbService.js for the full writeup.
        if (data.producers.length > 0) {
            const producerNames = data.producers.map(p => p.name);
            const producerPeople = await findOrCreatePeople(firestore, auth, 'directors', producerNames);
            const producersWithTitle = producerPeople.map(p => {
                const match = data.producers.find(dp => dp.name === p.name);
                return (match && match.title) ? { ...p, title: match.title } : p;
            });
            setSelectedProducers(producersWithTitle);
        }

        if (data.cast.length > 0) {
            const castNames = data.cast.map(c => c.name);
            const castPeople = await findOrCreatePeople(firestore, auth, 'cast', castNames);
            const castWithRoles = castPeople.map(p => {
                const match = data.cast.find(dc => dc.name === p.name);
                return (match && match.role) ? { ...p, role: match.role } : p;
            });
            setSelectedCast(castWithRoles);
        }

        if (data.posterUrl) {
            try {
                const imgResponse = await fetch(data.posterUrl);
                const imgBlob = await imgResponse.blob();
                if (artworkImgRef.current) {
                    artworkImgRef.current.src = URL.createObjectURL(imgBlob);
                }
                setFieldValue('posterShadow', true);
                setArtworkImgFile(imgBlob);

                try {
                    const resizedImage = await resizeFile(imgBlob);
                    setArtworkImgFile(resizedImage);
                } catch (resizeError) {
                    console.warn('[populateFromTmdbTv] poster resize failed, uploading original image instead:', resizeError);
                }
            } catch (e) {
                console.warn('[populateFromTmdbTv] poster fetch failed:', e);
            }
        }

        const gaps = [];
        if (!data.overview) gaps.push('synopsis');
        if (!data.directors.length) gaps.push('episode director');
        if (!data.writers.length) gaps.push('episode writer');
        if (!data.posterUrl) gaps.push('poster');

        setTmdbFetchStatus('success');
        setTmdbFetchNote(
            gaps.length > 0
                ? `Pulled "${displayTitle}" from TMDB, but it's missing: ${gaps.join(', ')} -- fill those in by hand.`
                : `Pulled "${displayTitle}" from TMDB.`
        );
    }

    const showAddProducer = (e) => {
        e.stopPropagation();

        setModalProducerOpen(true);
    }

    const addProducerHandler = async (item) => {
        setModalProducerOpen(false);

        // validate
        if (!item || !item.guid || !item.name || !item.title) {
            return;
        }

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","title":"Producer"}
        await setSelectedProducers([item, ...selectedProducers]);
    }

    const addActorHandler = async (item) => {
        setModalActorOpen(false);

        // validate
        if (!item || !item.guid || !item.name) {
            return;
        }

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","role":"Producer"}
        await setSelectedCast([item, ...selectedCast]);
    }

    const addWatchHandler = async (item) => {
        setModalWatchOpen(false);

        // validate
        if (!item || !item.title || !item.url) {
            return;
        }

        // will receive:  {"guid":"wpjc8t3wRlPdP3NRigtS","name":"Christopher Nolan","role":"Producer"}
        await setSelectedWatch([item, ...selectedWatch]);
    }

    const countryHandler = async (item, setFieldValue, setFieldTouched) => {
        if (item && item.id && item.id.length === 2) {
            // window.alert(JSON.stringify(item));
            await setFieldValue('country', item.id);
            await setFieldTouched('country', true);
        } else {
            window.alert("Country item not received");
        }
    }

    const languageHandler = async (item, setFieldValue, setFieldTouched) => {
        if (item && item.id && item.id.length === 2) {
            // window.alert(JSON.stringify(item));
            await setFieldValue('language', item.id);
            await setFieldTouched('language', true);
        } else {
            window.alert("Language item not received");
        }
    }

    return (
        <>
            {modalSpinnerOpen && <ModalLoading open={modalSpinnerOpen} message={modalMessage} />}
            {modalProducerOpen && <AddProducerModal isOpen={modalProducerOpen} handler={addProducerHandler} />}
            {modalActorOpen && <AddActorModal isOpen={modalActorOpen} handler={addActorHandler} />}
            {modalWatchOpen && <AddWatchModal isOpen={modalWatchOpen} handler={addWatchHandler} />}

            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} validateOnMount={true}>
                {({ handleSubmit, handleChange, handleBlur, isValid, dirty, isSubmitting, errors, setFieldValue, setFieldTouched, values }) => (
                    <form onSubmit={handleSubmit} className="">

                        <div className="space-y-12 sm:space-y-16">
                            <div>
                                <div className="sticky top-0 z-30 py-2 bg-white border-b border-gray-300">
                                    <div className='sm:flex sm:items-center sm:justify-between'>
                                        <h1 className="my-2 text-2xl font-bold text-gray-900 sm:text-3xl">Add New Title</h1>
                                        <div className="mt-3 flex sm:mt-0 sm:ml-4">
                                            <button type="button" onClick={cancelPressed} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-isober-500">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="my-2 max-w-2xl text-sm leading-6 text-gray-600">
                                            {debugShowFormErrors && errors && Object.keys(errors).length > 0 && Object.keys(errors).map((key) => (
                                                <span key={key} className="text-orange-500">{key}: {errors[key]} ; &nbsp;</span>
                                            ))}
                                        </p>
                                    </div>
                                </div>

                                {errorMessage && <ErrorBanner message={errorMessage} />}

                                <div className="mt-2 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:pb-0">
                                    {/* 2026-08-20: Kind moved above the TMDB field, per TV series support --
                                        it now determines how that very first field behaves (movie vs. TV
                                        URL), so it needs to be set before Chris pastes a link, not after. */}
                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Kind &nbsp;
                                            <ErrorMessage name="kind" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <input id="kind-movie" name="kind" value="1" onClick={() => { setUseSeriesMetadata(false); }} defaultChecked={initialValues.kind === "1"} type="radio" onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="kind-movie" className="block text-sm leading-6 text-gray-900">
                                                    Movie
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-x-3">
                                                <input id="kind-series" name="kind" value="2" onClick={() => { setUseSeriesMetadata(true); }} defaultChecked={initialValues.kind === "2"} type="radio" onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="kind-series" className="block text-sm leading-6 text-gray-900">
                                                    Series
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2026-08-19: TMDB field moved from the bottom of the form
                                        (next to the trailer field) to the very top, per Pascal --
                                        pasting the TMDB URL is now step one of the bulk-entry flow,
                                        auto-filling everything below for review.
                                        2026-08-20: relabels to "TV Series URL" and parses/fetches
                                        against TMDB's /tv/ endpoints instead of /movie/ when Kind is
                                        Series -- same underlying idTMDB field and siteIds.tmdb storage
                                        either way, see handleTmdbFieldChange. */}
                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="idTMDB" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            {useSeriesMetadata ? 'TV Series URL' : 'Movie Database ID'} <br />
                                            <span className='text-gray-400'>{useSeriesMetadata ? 'Start here -- paste the TMDB TV URL, then pick a Season below, to auto-fill the form' : 'Start here -- paste the TMDB URL to auto-fill the form'}</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                    <input ref={idTMDBRef} type="text" name="idTMDB" id="idTMDB" autoComplete="none" onChange={(e) => handleTmdbFieldChange(e, setFieldValue, setFieldTouched, values)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder={useSeriesMetadata ? '/tv/1396-breaking-bad, /tv/1396-breaking-bad/season/3, or full URL' : '/movie/184-jackie-brown or full URL'} />
                                                </div>
                                                <img src="/images/Partner_TMDB.png" alt="" />
                                                <ErrorMessage name="idTMDB" component="span" className="text-red-700 italic" />
                                            </div>
                                            {tmdbFetchStatus === 'loading' && (
                                                <p className="mt-1 text-sm text-gray-500">Fetching from TMDB...</p>
                                            )}
                                            {tmdbFetchStatus === 'success' && (
                                                <p className="mt-1 text-sm text-green-700">{tmdbFetchNote}</p>
                                            )}
                                            {tmdbFetchStatus === 'error' && (
                                                <p className="mt-1 text-sm text-red-700">{tmdbFetchNote}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* 2026-08-20: Season -- required in Series mode, since it's what makes
                                        each season its own title entry. Plain 1-99 list per Pascal's spec
                                        rather than dynamically capped to the fetched series' actual season
                                        count -- simpler and one less thing that can silently disagree with
                                        what's actually pasted above. Re-fetches on change if a valid TV URL
                                        is already in place, same as changing the URL re-fetches on whatever
                                        season is already selected -- whichever field Chris fills in second
                                        is what triggers the auto-fill. */}
                                    {useSeriesMetadata && <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="season" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Season &nbsp;
                                            <ErrorMessage name="season" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {/* 2026-08-29 (Chris, urgent): "Limited Series" and "Specials" added
                                                above the plain numbered seasons. Bound to seasonSelection, NOT
                                                season -- both Limited Series and a plain Season 1 store
                                                season=1 underneath (see handleSeasonFieldChange), so the select
                                                needs its own state to know which one to show as selected. */}
                                            <select name="season" id="season" value={values.seasonSelection || ''} onChange={(e) => handleSeasonFieldChange(e, setFieldValue, setFieldTouched, values)} onBlur={handleBlur} className="block w-full max-w-xs rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                                                <option value="">Choose a season...</option>
                                                <option value="limited">Limited Series</option>
                                                <option value="specials">Specials</option>
                                                {Array.from({ length: 99 }, (_, i) => i + 1).map(n => (
                                                    <option key={n} value={n}>Season {n}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>}

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="title-display" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Display title &nbsp;
                                            <ErrorMessage name="titleDisplay" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="">
                                                <input ref={titleDisplayRef} type="text" name="titleDisplay" id="titleDisplay" autoComplete="off" defaultValue={initialValues.titleDisplay} onChange={handleChange} onBlur={(e) => titleDisplayBlurred(e, handleBlur, values)} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                            </div>
                                        </div>

                                        {duplicateWarnings.length > 0 && (
                                            <div className="sm:col-span-3 mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                                <div className="flex">
                                                    <div className="flex-shrink-0">
                                                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-yellow-800">
                                                            {duplicateWarnings[0].ratio === 1 ? "A title matching this one already exists:" : "A similar title already exists:"}
                                                        </p>
                                                        <ul className="mt-1 text-sm text-yellow-700">
                                                            {duplicateWarnings.map((match) => (
                                                                <li key={match.id}>
                                                                    <a href={`/app/title/${match.id}`} target="_blank" rel="noreferrer" className="underline hover:text-yellow-900">
                                                                        {match.title}{match.year !== undefined && ` (${match.year})`}
                                                                    </a>
                                                                    {match.yearsDiffer && <span className="italic"> -- different year, might be a different production</span>}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <p className="mt-1 text-sm text-yellow-700">This is just a heads-up -- you can still save if this is a legitimate separate title (remake, different year, etc.).</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Synopsis &nbsp;
                                            <ErrorMessage name="synopsis" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <textarea ref={synopsisRef} id="synopsis" name="synopsis" rows={4} autoComplete="off" defaultValue={initialValues.synopsis} onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="about" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Display on &nbsp;
                                            <ErrorMessage name="channelBlackphlix" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <input id="channel-blackphlix" name="channelBlackphlix" type="checkbox" defaultChecked={initialValues.channelBlackphlix} onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="channel-blackphlix" className="block text-sm leading-6 text-gray-900">
                                                    BlackPhlix
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-x-3">
                                                <input id="channel-findflix" name="channelFindflix" type="checkbox" defaultChecked={initialValues.channelFindflix} onChange={handleChange} onBlur={handleBlur} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                                <label htmlFor="channel-findflix" className="block text-sm leading-6 text-gray-900">
                                                    FindFlix
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            {/* 2026-08-20: relabeled in Series mode -- TMDB has no single
                                                "director" for a season the way a movie has one; this field
                                                holds the aggregated distinct episode directors instead, see
                                                populateFromTmdbTv in tmdbService.js */}
                                            {useSeriesMetadata ? 'Episode Director(s)' : 'Director(s)'} &nbsp;
                                            <ErrorMessage name="directorsGuids" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedDirectors !== undefined && Array.isArray(selectedDirectors) && selectedDirectors.length > 0) && <div>
                                                {selectedDirectors.map((director, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{director.name}</span>
                                                        <button type="button" onClick={() => clearDirectorPressed(director, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleDirectorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="writers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Writer(s) &nbsp;
                                            <ErrorMessage name="writersGuids" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedWriters !== undefined && Array.isArray(selectedWriters) && selectedWriters.length > 0) && <div>
                                                {selectedWriters.map((writer, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{writer.name}</span>
                                                        <button type="button" onClick={() => clearWriterPressed(writer, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleWriterChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="cinematographers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Cinematographer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedCinematographers !== undefined && Array.isArray(selectedCinematographers) && selectedCinematographers.length > 0) && <div>
                                                {selectedCinematographers.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearCinematographerPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleCinematographerChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="editors" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Editor(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedEditors !== undefined && Array.isArray(selectedEditors) && selectedEditors.length > 0) && <div>
                                                {selectedEditors.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearEditorPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleEditorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        <label htmlFor="composers" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Composer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedComposers !== undefined && Array.isArray(selectedComposers) && selectedComposers.length > 0) && <div>
                                                {selectedComposers.map((person, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{person.name}</span>
                                                        <button type="button" onClick={() => clearComposerPressed(person, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <DirectorAutocomplete handler={handleComposerChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                        </div>

                                        {useSeriesMetadata === true && <>
                                            {/* 2026-08-20 (Pascal): merged with Showrunner(s) into this one
                                                field/list -- both terms point at the same TMDB credit
                                                (created_by) and only `creators` is ever rendered by either
                                                shipped app, so a separate Showrunner(s) control was populating
                                                data nobody could see. Still written to showrunners/
                                                showrunnersIdx too on submit, for backward compatibility --
                                                see handleSubmit. */}
                                            <label htmlFor="creators" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                                Creator(s) &nbsp;
                                                <ErrorMessage name="creators" component="span" className="text-red-700 italic" />
                                            </label>
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                {(selectedCreators !== undefined && Array.isArray(selectedCreators) && selectedCreators.length > 0) && <div>
                                                    {selectedCreators.map((creator, index) => (
                                                        <div key={index}>
                                                            <span className='text-green-700 pl-1'>{creator.name}</span>
                                                            <button type="button" onClick={() => clearCreatorPressed(creator, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>}
                                                <DirectorAutocomplete handler={handleCreatorChange} sfv={setFieldValue} sft={setFieldTouched} showCreateOption={true} userinfo={authUser} />
                                            </div>

                                            <label htmlFor="rating" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                                Number of episodes <br />
                                                <span className='text-gray-400'>Optional</span> <br />
                                                <ErrorMessage name="numEpisodes" component="span" className="text-red-700 italic" />
                                            </label>
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                    <div className="flex items-center gap-x-3">
                                                        <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                            <input ref={numEpisodesRef} type="text" name="numEpisodes" id="numEpisodes" defaultValue={initialValues.numEpisodes} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </>}

                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Producer(s) <br />
                                            <span className='text-gray-400'>Optional</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedProducers !== undefined && Array.isArray(selectedProducers) && selectedProducers.length > 0) && <div>
                                                {selectedProducers.map((producer, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{producer.title}: {producer.name}</span>
                                                        <button type="button" onClick={() => clearProducerPressed(producer, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddProducer} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Cast &nbsp;
                                            <ErrorMessage name="cast" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedCast !== undefined && Array.isArray(selectedCast) && selectedCast.length > 0) && <div>
                                                {selectedCast.map((actor, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{actor.name} {actor.role && <span>: {actor.role}</span>}</span>
                                                        <button type="button" onClick={() => clearCastPressed(actor, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddActor} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add actor
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="synopsis" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Where to watch  <br />
                                            <span className='text-gray-400'>Optional</span> <br />
                                            <ErrorMessage name="watch" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            {(selectedWatch !== undefined && Array.isArray(selectedWatch) && selectedWatch.length > 0) && <div>
                                                {selectedWatch.map((watchItem, index) => (
                                                    <div key={index}>
                                                        <span className='text-green-700 pl-1'>{watchItem.title} at: <u>{watchItem.url}</u></span>
                                                        <button type="button" onClick={() => clearWatchPressed(watchItem, setFieldValue, setFieldTouched)} className="mx-3 my-2 rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-600 shadow-sm hover:bg-green-100">
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>}
                                            <button type="button" onClick={showAddWatch} className="inline-flex gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                <PlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                                                Add item
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="released" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Year of Release &nbsp;
                                            <ErrorMessage name="released" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                <input ref={releasedRef} type="text" name="released" id="released" autoComplete="none" maxLength={4} inputMode="numeric" onChange={handleChange} onBlur={(e) => releasedBlurred(e, handleBlur)} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="YYYY" />
                                            </div>
                                        </div>

                                        <label htmlFor="runtime" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            {useSeriesMetadata ? 'Episode duration' : 'Runtime'} &nbsp;
                                            <ErrorMessage name="runtimeHH" component="span" className="text-red-700 italic" /> &nbsp;
                                            <ErrorMessage name="runtimeMM" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="flex items-center gap-x-3">

                                                    <div className="relative mt-2 rounded-md shadow-sm">
                                                        <input ref={runtimeHHRef} type="text" name="runtimeHH" id="runtimeHH" defaultValue={initialValues.runtimeHH} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 pl-2 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="0" />
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <span className="text-gray-500 sm:text-sm" id="price-currency">
                                                                Hours
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="relative mt-2 rounded-md shadow-sm">
                                                        <input ref={runtimeMMRef} type="text" name="runtimeMM" id="runtimeMM" defaultValue={initialValues.runtimeMM} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block w-full rounded-md border-0 py-1.5 pl-2 pr-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="0" />
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                            <span className="text-gray-500 sm:text-sm" id="price-currency">
                                                                Minutes
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <label htmlFor="rating" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            Rating &nbsp;
                                            <ErrorMessage name="rating" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="mt-2 sm:col-span-2 sm:mt-0">
                                                <div className="flex items-center gap-x-3">
                                                    <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                        <input type="text" name="rating" id="rating" ref={ratingFieldRef} defaultValue={initialValues.rating} autoComplete="none" onChange={handleChange} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="PG13" />
                                                    </div>
                                                    <div>
                                                        <input type="checkbox" id="ratingNotRated" onChange={(e) => { notRatedChanged(e, setFieldValue, setFieldTouched) }} className='mx-2 focus:ring-isober-500 h-4 w-4 text-isober-600 border-gray-300 rounded' />
                                                        <label htmlFor="ratingNotRated" className='text-sm font-medium text-gray-700'>Not rated</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Country of origin
                                            <br /><span className='text-gray-400'>Optional</span>
                                            <ErrorMessage name="origin" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <CountryChooser initialValue={values.country} handler={countryHandler} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                                        </div>

                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Original language
                                            <br /><span className='text-gray-400'>Optional</span>
                                            <ErrorMessage name="language" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <LanguageChooser initialValue={values.language} handler={languageHandler} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="trailer" className="block text-sm font-medium leading-6 text-gray-900">
                                            Youtube trailer ID &nbsp;
                                            <ErrorMessage name="trailer" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                <input type="text" name="trailer" id="trailer" autoComplete="none" onChange={(e) => handleTrailerFieldChange(e, setFieldValue)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="Youtube ID or full URL" />
                                            </div>
                                        </div>

                                    </div>

                                    {/* 2026-08-19: visible IMDB id field -- auto-filled from TMDB,
                                        manually correctable, accepts a full imdb.com URL paste.
                                        Stored in siteIds.imdb so the app can show the IMDB link. */}
                                    <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                        <label htmlFor="idIMDB" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                            IMDB ID <br />
                                            <span className='text-gray-400'>Fills automatically from TMDB -- paste an IMDB URL to set it manually</span>
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <div className="flex items-center gap-x-3">
                                                <div className="my-1 pl-2 flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                                                    <input ref={idIMDBRef} type="text" name="idIMDB" id="idIMDB" autoComplete="none" onChange={(e) => handleImdbFieldChange(e, setFieldValue)} onBlur={handleBlur} className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6" placeholder="tt1234567 or full URL" />
                                                </div>
                                                <ErrorMessage name="idIMDB" component="span" className="text-red-700 italic" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Genres <br />
                                            <span className='text-gray-400'>Choose at least one</span> &nbsp;
                                            <ErrorMessage name="genres" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <Field name="validdevices" component={GenreChooser} initialSelection={initialValues.genres} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Tags <br />
                                            <span className='text-gray-400'>Choose at least one</span> &nbsp;
                                            <ErrorMessage name="tags" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <Field name="validdevices" component={TagChooser} initialSelection={initialValues.tags} />
                                        </div>
                                    </div>

                                    <div className="sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6">
                                        <label htmlFor="photo" className="block text-sm font-medium leading-6 text-gray-900">
                                            Poster &nbsp;
                                            <ErrorMessage name="posterShadow" component="span" className="text-red-700 italic" />
                                        </label>
                                        <div className="mt-2 sm:col-span-2 sm:mt-0">
                                            <input id="file" name="file" type="file" accept="image/png, image/jpeg" onChange={(e) => handleArtworkImageUpload(e, setFieldValue)} />

                                            <div className='mt-2 font-medium text-sm leading-6 text-gray-900'>
                                                <img ref={artworkImgRef} className='w-40 h-60 border-none border-0' alt="" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Button actions */}
                        <div className="mt-4 flex justify-end items-center gap-x-4">
                            {/* 2026-08-19: makes "why won't this save" self-diagnosable --
                                previously the only way to see this was flipping the
                                hardcoded debugShowFormErrors flag and redeploying.
                                2026-08-19 (v2): the first version only covered the !isValid
                                case, so it went silent -- while the button stayed disabled
                                -- whenever the real cause was !dirty or a stale isValid=false
                                with an empty errors object. This covers all three disabling
                                conditions on the button below so it can never show nothing
                                while Create title is greyed out. */}
                            {(isSubmitting || !dirty || !isValid) && (
                                <div className="text-sm text-red-700">
                                    {isSubmitting && 'Submitting…'}
                                    {!isSubmitting && !dirty && 'Needs: change/fill in a field first (form not dirty yet)'}
                                    {!isSubmitting && dirty && !isValid && (
                                        Object.keys(errors).length > 0
                                            // 2026-08-20: directorsGuids reads as "Episode Director(s)" in Series
                                            // mode here too, matching the field's own on-page label.
                                            ? `Needs: ${Object.keys(errors).map(key => (key === 'directorsGuids' && useSeriesMetadata) ? 'Episode Director(s)' : (FIELD_LABELS[key] || key)).join(', ')}`
                                            : 'Form marked invalid but no field errors reported -- validation may still be settling, try again in a second'
                                    )}
                                </div>
                            )}
                            <button type="submit" disabled={isSubmitting || !dirty || !isValid} className="disabled:opacity-60 bg-indigo-800 hover:bg-indigo-900 py-2 px-4 border border-indigo-900 rounded-md shadow-sm text-sm font-medium text-white disabled:text-gray-2    00 hover:bg-indigo-1000 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                Create title
                            </button>
                        </div>
                    </form>
                )
                }
            </Formik >
        </>
    );
}